<script setup>
import { ref, onMounted } from 'vue';
import { useAuthentication } from '@/composables/use-authentication';
// import { useFacilities } from '@/composables/use-facilities';
import { useOnline } from '@vueuse/core';
import { useNotifsTailed } from '@/composables/use-notifs';
import UserMenu from '@/components/commons/user-menu';
import MainMenu from '@/components/main-menu';
import useGlobalSnack from '@/components/commons/global-snack/composables';
import GlobalSnackbar from '@/components/commons/global-snack';

const isOnline = useOnline();

// const { getUserFacilities, facilities, getActiveFacility } = useFacilities();
const { show } = useGlobalSnack();

useNotifsTailed({
  onNewNotif: (notif) => {
    console.warn('New notif', notif);
    show(notif.title, { type: 'info' });
  },
});

const {
  doctorWelcomeOnboardingDone,
  updateOnboarding,
  init: initCurrentUser,
} = useAuthentication();

const blockerDialog = ref(false);

onMounted(() => init());

async function init() {
  await initCurrentUser();
  // await getUserFacilities();
  // await getActiveFacility();
  
  if (doctorWelcomeOnboardingDone.value === false) {
    welcomeDialog.value = true;
  }
}

const welcomeDialog = ref(false);
const welcomeLoading = ref(false);

async function onWelcomeAccepted () {
  try {
    welcomeLoading.value = true;
    await updateOnboarding('doctorOnboarding.welcome');
    welcomeDialog.value = false;
  } catch (e) {
    console.error('Something went wrong. Try again later.');
  } finally {
    welcomeLoading.value = false;
  }
}

const drawer = ref(false);
const sidebarCollapsed = ref(false);

const toggleSidebar = () => {
  sidebarCollapsed.value = !sidebarCollapsed.value;
};
</script>

<template>
  <div data-theme="parmazip">
    <div v-if="welcomeDialog" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div class="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-auto">
        <div class="flex gap-4 p-8">
          <div class="flex-1 flex flex-col gap-4">
            <h1 class="text-xl text-neutral font-bold">Welcome to Parmazip</h1>
            <p class="text-neutral-400">
              We built Parmazip with a mission to make primary care more accessible to everyone, and for pharmacists like you to harness the power of the latest technologies available today that could make your practice less hard.  Starting with telepharmacy, we're envisioning on-demand virtual care to be a norm in every household.
            </p>
            <p class="text-neutral-400">
              As the pharmacist's role expands over time, we are dedicated to support you with the latest tools and experience a new era of pharmacy practice. With Parmazip, you are at the forefront of modern pharmacy practice.
            </p>
            <button type="button" class="pz-btn-filled w-[200px] text-sm"
              :disabled="welcomeLoading" @click="onWelcomeAccepted">
              <span v-if="!welcomeLoading">Let's get started!</span>
              <span v-else class="flex items-center gap-2">
                <i class="las la-spinner animate-spin"></i>
                Loading...
              </span>
            </button>
          </div>
          <div class="flex-1 flex items-center gap-2">
            <template v-if="isOnline">
              <vue-3-lottie
                animationLink="https://firebasestorage.googleapis.com/v0/b/mc-v4-prod.appspot.com/o/lottie%2Feasyjoey%2Fonboarding-mini-encounter.json?alt=media&token=5666aa86-a4bb-4061-a91d-2eb097c11e65"
                hover :speed="0.2" :loop="false" />
            </template>
            <template v-else>
              <img class="w-full" src="../assets/images/easyjoey-encounter-art.png" alt="">
            </template>
          </div>
        </div>
      </div>
    </div>
    <!-- DIALOGS END -->

    <div class="flex h-screen">
      <!-- Mobile overlay -->
      <div v-if="drawer" class="fixed inset-0 z-40 lg:hidden" @click="drawer = false">
        <div class="absolute inset-0 bg-black opacity-50"></div>
      </div>

      <!-- Sidebar -->
      <div
        class="fixed inset-y-0 left-0 z-50 bg-gradient-to-b from-white to-gray-50 h-screen transition-all duration-300 overflow-hidden transform lg:transform-none flex-shrink-0"
        :class="[
          sidebarCollapsed ? 'w-[64px] min-w-[64px] max-w-[64px]' : 'w-[320px] min-w-[320px] max-w-[320px]',
          drawer ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        ]"
      >
          <!-- Logo Header -->
          <header 
            class="flex justify-center items-center z-10 h-16 border-b border-red overflow-hidden"
            :class="sidebarCollapsed ? 'px-2' : 'px-4'"
          >
            <div v-if="!sidebarCollapsed" class="flex-1">
              <img 
                class="h-[40px] hover:cursor-pointer transition-transform hover:scale-105" 
                src="../assets/images/parmazip/logo-with-text.png" 
                alt="Parmazip Logo"
                @click="$router.push('/dashboard')" 
              />
            </div>
            <div v-else class="flex justify-center w-full">
              <div 
                class="w-10 h-10 bg-gradient-to-br from-primary to-red-700 rounded-xl flex items-center justify-center cursor-pointer"
                @click="$router.push('/dashboard')"
              >
                <i class="las la-home text-white text-xl"></i>
              </div>
            </div>
          </header>
          <MainMenu 
            @close="drawer = false;"
            :collapsed="sidebarCollapsed"
            :toggle-sidebar="toggleSidebar"
          />
      </div>

      <div class="flex flex-col w-full" :class="sidebarCollapsed ? 'lg:ml-[64px]' : 'lg:ml-[320px]'">

        <header
          class="nav flex bg-gradient-to-r from-white via-white to-gray-50/50 border-b border-gray-200 justify-between lg:justify-end items-center fixed top-0 right-0 z-30 h-16 backdrop-blur-md"
          :class="sidebarCollapsed ? 'left-0 lg:left-[64px] w-full lg:w-[calc(100%-64px)]' : 'left-0 lg:left-[320px] w-full lg:w-[calc(100%-320px)]'">

          <div class="flex-none lg:hidden">
            <button
              @click="drawer = !drawer"
              aria-label="open sidebar"
              class="p-2 rounded-md hover:bg-gradient-to-r hover:from-gray-100 hover:to-gray-200 transition-all duration-200 group"
            >
              <i class="las la-bars text-xl text-gray-700 group-hover:text-gray-900 transition-colors"></i>
            </button>
          </div>
          <div class="flex-1 lg:hidden">
            <!-- Search placeholder for future implementation (hidden on large screens) -->
          </div>
          <div class="flex-none flex items-center justify-center">
            <user-menu />
          </div>
        </header>

        <div class="flex flex-col min-h-screen bg-gray-50 pt-16">
          <main class="flex-1 p-4 lg:p-6 max-w-full w-full mx-auto">
            <NuxtPage />
          </main>
        </div>
      </div>
    </div>
  </div>
  <global-snackbar />
</template>
